import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import clsx from 'clsx';
import type { JSX, ReactNode } from 'react';
import { trackMobileFilterDrawerEvent } from '@/lib/analytics/events';

export interface MobileFilterDrawerControl {
  id: string;
  control: ReactNode;
}

export interface MobileFilterDrawerChip {
  id: string;
  label: string;
  onRemove?: () => void;
}

interface MobileFilterDrawerProps {
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controls: MobileFilterDrawerControl[];
  summaryChips?: MobileFilterDrawerChip[];
  onApply: () => void;
  onReset: () => void;
  applyLabel?: string;
  resetLabel?: string;
  analyticsId?: string;
  isApplyDisabled?: boolean;
}

/**
 * Reusable Radix-powered drawer for advanced mobile filters with analytics instrumentation.
 */
export function MobileFilterDrawer({
  title,
  open,
  onOpenChange,
  controls,
  summaryChips = [],
  onApply,
  onReset,
  applyLabel = 'Apply filters',
  resetLabel = 'Reset',
  analyticsId = 'mobile-filter-drawer',
  isApplyDisabled = false,
}: MobileFilterDrawerProps): JSX.Element {
  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    trackMobileFilterDrawerEvent({
      drawerId: analyticsId,
      action: nextOpen ? 'open' : 'close',
    });
  };

  const handleApply = () => {
    trackMobileFilterDrawerEvent({ drawerId: analyticsId, action: 'apply' });
    onApply();
    handleOpenChange(false);
  };

  const handleReset = () => {
    trackMobileFilterDrawerEvent({ drawerId: analyticsId, action: 'reset' });
    onReset();
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-surface-navy/40 backdrop-blur-sm" />
        <Dialog.Content
          aria-describedby={undefined}
          className={clsx(
            'fixed inset-x-0 bottom-0 z-50 mx-auto flex w-full max-w-full flex-col gap-6 rounded-t-3xl sm:max-w-screen-sm',
            'bg-surface-light px-5 pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] pt-4 shadow-card',
          )}
        >
          <div className="flex items-center justify-between">
            <div className="mx-auto h-1.5 w-12 rounded-full bg-surface-subtle" aria-hidden />
          </div>
          <div className="flex items-start justify-between gap-4">
            <Dialog.Title className="text-lg font-semibold text-brand-primary">
              {title}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-surface-subtle text-brand-primary transition hover:border-brand-primary/40 hover:bg-brand-primary/5"
                aria-label="Close filters"
              >
                <Cross2Icon className="h-4 w-4" aria-hidden />
              </button>
            </Dialog.Close>
          </div>

          {summaryChips.length > 0 ? (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap gap-2">
                {summaryChips.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className="inline-flex min-h-11 items-center gap-2 rounded-full border border-brand-primary/20 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary"
                    onClick={() => {
                      chip.onRemove?.();
                    }}
                  >
                    <span>{chip.label}</span>
                    {chip.onRemove ? (
                      <Cross2Icon className="h-3 w-3 text-brand-primary/70" aria-hidden />
                    ) : null}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="text-xs font-semibold uppercase tracking-wide text-brand-primary underline-offset-2 hover:underline"
                onClick={handleReset}
              >
                {resetLabel}
              </button>
            </div>
          ) : null}

          <div className="flex flex-col gap-4 overflow-y-auto">
            {controls.map((control) => (
              <div key={control.id}>{control.control}</div>
            ))}
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="w-full rounded-2xl border border-brand-primary/20 px-4 py-3 text-sm font-semibold uppercase tracking-wide text-brand-primary transition hover:border-brand-primary/40 hover:bg-brand-primary/5"
            >
              {resetLabel}
            </button>
            <button
              type="button"
              disabled={isApplyDisabled}
              onClick={handleApply}
              className="w-full rounded-2xl bg-brand-primary px-4 py-3 text-sm font-semibold uppercase tracking-wide text-text-inverted transition disabled:cursor-not-allowed disabled:opacity-60"
            >
              {applyLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
